import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import {
  selectBasicQuestions,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { useGetManufacturersLazyQuery } from "@covertree/new-business-graphql";
import { FormAsyncSelect } from "@covertree/ui";

import { HomeDetailsForm } from "../validatorsAndTypes/homeDetailsAndConstructionForm";

interface Props {
  name?: string;
  label?: string;
  placeholder?: string;
  tooltip?: string;
  disabled?: boolean;
  fontSize?: number;
  labelFontSize?: number;
}

export function ManufacturerSelect({
  name = "manufacturer",
  label = "Make",
  placeholder = "Type at least 3 characters",
  tooltip = "Select the home manufacturer (make) from the list. If not listed, choose 'Not listed' and enter a custom make.",
  disabled = false,
  fontSize,
  labelFontSize,
}: Props) {
  const { manufacturer } = useNewBusinessSelector(selectBasicQuestions);

  const { control } = useFormContext<HomeDetailsForm>();

  const [getManufacturers, { data, loading, previousData }] =
    useGetManufacturersLazyQuery();

  const previousManufacturersData = useMemo(() => {
    if (!previousData) return [];

    if (!previousData.getParameters.includes("Not Listed")) {
      return ["Not Listed", ...previousData.getParameters];
    }

    return previousData.getParameters;
  }, [previousData]);

  const manufacturersData = useMemo(() => {
    if (!data) return [];

    if (!data.getParameters.includes("Not Listed")) {
      return ["Not Listed", ...data.getParameters];
    }

    return data.getParameters;
  }, [data]);

  return (
    <FormAsyncSelect
      disabled={disabled}
      fontSize={fontSize}
      labelFontSize={labelFontSize}
      id="manufacturer-field"
      dataTestId="manufacturer-input"
      name={name}
      defaultSearch="America"
      label={manufacturer?.label ?? label}
      placeholder={placeholder}
      tooltip={manufacturer?.tooltip ?? tooltip}
      data={manufacturersData}
      initialData={previousManufacturersData}
      loading={loading}
      control={control}
      inputStyle={{
        padding: "0px !important",
      }}
      inputProps={{
        sx: {
          padding: "0 13px 0 14px !important",
        },
      }}
      onGetAsyncData={getManufacturers}
    />
  );
}
